import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Container, Col, Form, Row } from 'react-bootstrap';

import Loader from '../../../components/Loader';

import NewProviderModal from '../../../components/modals/ProviderModals/NewProviderModal';
import LinkProviderAccountModal from '../../../components/modals/LinkProviderAccountModal';
import UtilityTable from './UtilityTable';
import RequestCredentialModal from '../../../components/modals/ProviderModals/RequestCredentialModal';
import ProviderShareOrganizationModal from '../../../components/modals/ProviderShareOrganizationModal';
import { useOrganizationRoles } from '../../../helpers/checkRoles';
import {
  handleGetLicenseKey,
  handleGetAllCredentials,
  handleRefreshAccountsData,
  handleJoinLocalAccounts,
} from './helpers/provider';

function UtilityImportSettings(props) {
  const { organization, organizationId, provider_type, showToast, isAdmin } =
    props;

  // state variables
  const [allCredentials, setAllCredentials] = useState([]);
  const [credentialIds, setCredentialIds] = useState([]);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [providerAccountsWithLocal, setProviderAccountsWithLocal] = useState(
    []
  );
  const [localUtilityAccounts, setLocalUtilityAccounts] = useState([]);
  const [posr, setposr] = useState(null); //provider organization share recipient
  const [poso, setposo] = useState(null); //provider organization share owner
  const [hiddenCredentials, setHiddenCredentials] = useState(null);

  //state flags
  const [showSecondary, setShowSecondary] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  //modal flags
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showRequestCredentialModal, setShowRequestCredentialModal] =
    useState(false);
  const [showShareCredentialModal, setShowShareCredentialModal] =
    useState(false);

  //licenses, loading, access
  const [activeLicense, setActiveLicense] = useState(false);
  const [isLoadingCreds, setIsLoadingCreds] = useState(true);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  const updateAccess = useOrganizationRoles(
    organization,
    'organization',
    'update'
  );

  useEffect(() => {
    let activeLicense = false;
    let license_key = handleGetLicenseKey(provider_type);
    if (organization.id) {
      if (organization && organization.licenses) {
        activeLicense = organization.licenses[license_key].status === 'active';
      }
    }
    setActiveLicense(activeLicense);
  }, [organization, provider_type]);

  useEffect(() => {
    if (organization.id) {
      setposr(organization.posr);
      setposo(organization.poso);
      setHiddenCredentials(organization.hidden_provider_credentials);
    }
  }, [
    organization.posr,
    organization.poso,
    organization.id,
    organization.hidden_provider_credentials,
  ]);

  // modal data
  const [linkModalData, setLinkModalData] = useState({
    id: null,
    enrolled: false,
    linkedAccount: null,
    accountNumber: '',
    utilityProvider: '',
    subscribe: true,
    pullHistory: true,
  });

  // modal functions
  const handleCloseNewProviderModal = () => {
    setShowNewProvider(false);
    getAllCredentials();
    refreshAccountsData();
  };

  const handleOpenLinkModal = (selectedRow) => {
    selectedRow.newAccountId = selectedRow.localAccount
      ? selectedRow.localAccount.id
      : null;
    setLinkModalData(selectedRow);
    setShowLinkModal(true);
  };

  //get all credentials functions
  const getAllCredentials = useCallback(() => {
    if (organizationId && posr && hiddenCredentials) {
      handleGetAllCredentials(
        setAllCredentials,
        setCredentialIds,
        organizationId,
        posr,
        hiddenCredentials,
        provider_type,
        setIsLoadingCreds,
        showToast
      );
    }
  }, [organizationId, posr, hiddenCredentials, showToast, provider_type]);

  useEffect(() => {
    getAllCredentials();
  }, [getAllCredentials]);

  // get account functions
  const refreshAccountsData = useCallback(() => {
    handleRefreshAccountsData(
      setProviderAccounts,
      setLocalUtilityAccounts,
      organizationId,
      provider_type,
      credentialIds,
      setIsLoadingAccounts,
      showToast
    );
  }, [organizationId, provider_type, credentialIds, showToast]);

  useEffect(() => {
    handleJoinLocalAccounts(
      providerAccounts,
      localUtilityAccounts,
      provider_type,
      setProviderAccountsWithLocal
    );
  }, [providerAccounts, localUtilityAccounts, provider_type]);

  useEffect(() => {
    refreshAccountsData();
  }, [refreshAccountsData]);

  return (
    <Card.Body>
      <Row>
        <Col style={{ marginBottom: '10px' }} xs={12}>
          <h5 style={{ marginBottom: 0 }}>
            Manage Data Providers
            {updateAccess && activeLicense && (
              <>
                <Button
                  data-id='new-util_btn'
                  className='float-right ml-3 btn-sm'
                  variant='success'
                  onClick={() => setShowNewProvider(true)}
                  disabled={isLoadingCreds}
                >
                  New Provider
                </Button>
                <Button
                  data-id='req-creds_btn'
                  className='float-right ml-3 btn-sm'
                  variant='primary'
                  onClick={() => setShowRequestCredentialModal(true)}
                  disabled={isLoadingAccounts}
                >
                  Request Credentials
                </Button>
                {isAdmin ? (
                  <Button
                    data-id='share-creds_btn'
                    className='float-right ml-3 btn-sm'
                    variant='primary'
                    onClick={() => setShowShareCredentialModal(true)}
                    disabled={isLoadingCreds}
                  >
                    Share Credentials
                  </Button>
                ) : (
                  ''
                )}
              </>
            )}
          </h5>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Check
            label='Show Hidden Credentials'
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
          />
          <Form.Check
            style={{ marginRight: '20px' }}
            label='Show Secondary Accounts'
            checked={showSecondary}
            onChange={(e) => setShowSecondary(e.target.checked)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {isLoadingCreds ? (
            <Container>
              <Row>
                <Col
                  style={{
                    paddingBottom: '10vh',
                    paddingTop: '5vh',
                    paddingLeft: '49%',
                  }}
                >
                  <Loader />
                </Col>
              </Row>
            </Container>
          ) : (
            <UtilityTable
              organizationId={organizationId}
              allCredentials={allCredentials}
              providerAccounts={providerAccountsWithLocal}
              provider_type={provider_type}
              showSecondary={showSecondary}
              showHidden={showHidden}
              handleOpenLinkModal={handleOpenLinkModal}
              isLoading={isLoadingAccounts}
              setAllCredentials={setAllCredentials}
              showToast={showToast}
              updateAccess={updateAccess}
              activeLicense={activeLicense}
            />
          )}
        </Col>
      </Row>
      <NewProviderModal
        organizationId={organizationId}
        show={showNewProvider}
        onHide={handleCloseNewProviderModal}
        provider_type={provider_type}
      />
      <LinkProviderAccountModal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        modalData={linkModalData}
        localUtilityAccounts={localUtilityAccounts}
        organization={organization}
        provider_type={provider_type}
        setProviderAccounts={setProviderAccounts}
        setLocalUtilityAccounts={setLocalUtilityAccounts}
        showToast={showToast}
        activeLicense={activeLicense}
      />
      <RequestCredentialModal
        show={showRequestCredentialModal}
        onHide={() => setShowRequestCredentialModal(false)}
        organizationId={organizationId}
        showToast={showToast}
        variant='new'
        provider_type={provider_type}
      />

      <ProviderShareOrganizationModal
        show={showShareCredentialModal}
        onHide={() => setShowShareCredentialModal(false)}
        organizationId={organizationId}
        refreshTable={getAllCredentials}
        provider_type={provider_type}
        poso={poso}
        setposo={setposo}
      />
    </Card.Body>
  );
}

export default UtilityImportSettings;
